import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";

function NumInput({ label, value, onChange, min = 1, max = 30, testId }) {
  const [raw, setRaw] = useState(String(value));
  const handleChange = (e) => {
    const v = e.target.value;
    if (v === "" || v === "-") { setRaw(v); return; }
    const n = parseInt(v, 10);
    if (!isNaN(n)) { setRaw(v); onChange(Math.max(min, Math.min(max, n))); }
  };
  const handleBlur = () => {
    const n = parseInt(raw, 10);
    const clamped = isNaN(n) ? min : Math.max(min, Math.min(max, n));
    setRaw(String(clamped)); onChange(clamped);
  };
  React.useEffect(() => { setRaw(String(value)); }, [value]);
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 block mb-2">{label}</label>
      <input data-testid={testId} type="number" inputMode="numeric" min={min} max={max} value={raw}
        onChange={handleChange} onBlur={handleBlur}
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 outline-none rounded-md px-4 py-3 text-lg" />
    </div>
  );
}

function ToggleGroup({ label, options, value, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 block mb-2">{label}</label>
      <div className="flex gap-2">
        {options.map(o => (
          <button key={o.value} onClick={() => onChange(o.value)}
            className={"flex-1 py-3 rounded-md font-bold uppercase tracking-wide text-sm transition-colors " +
              (value === o.value
                ? "bg-cyan-400 text-black"
                : "bg-zinc-950 border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white")}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SoloSetup() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [ends, setEnds] = useState(6);
  const [arrows, setArrows] = useState(6);
  const [series, setSeries] = useState(1);
  const [sessionName, setSessionName] = useState("");
  const [creating, setCreating] = useState(false);
  const [faceType, setFaceType] = useState("full");

  // Valeurs par défaut selon le blason
  const chooseFace = (ft) => {
    setFaceType(ft);
    if (ft === "full") { setEnds(6); setArrows(6); }
    else { setEnds(10); setArrows(3); }
  };

  const start = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/solo", {
        settings: {
          format: "solo",
          ends: ends || 6,
          arrows_per_end: arrows || 6,
          set_system: false,
          sets: 5,
          series: series || 1,
          session_name: sessionName.trim(),
          with_x: faceType !== "trispot_recurve",
          face_type: faceType,
          outer_ten: faceType !== "trispot_compound",
        },
      });
      navigate(`/solo/${data.solo_id}`);
    } catch {
      toast.error(t("solo_setup.error"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      <header className="sticky top-0 z-20 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <button data-testid="solo-back" onClick={() => navigate("/dashboard")} className="p-2 hover:bg-zinc-900 rounded-md">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="font-heading font-black text-xl uppercase tracking-tight">{t("dashboard.tile_solo_title")}</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        <div>
          <h1 className="font-heading font-black text-5xl uppercase tracking-tight">{t("solo_setup.heading")}</h1>
        </div>

        {/* Nom de session */}
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t("misc.session_name")} <span className="text-zinc-600">({t("misc.optional")})</span></label>
          <input data-testid="solo-session-name" type="text" value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            placeholder={t("misc.session_name_ph")}
            maxLength={80}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 outline-none rounded-md px-4 py-3 text-lg placeholder:text-zinc-700" />
        </div>

        {/* Blason */}
        <ToggleGroup label={t("targetcfg.face")}
          options={[
            { value: "full", label: t("targetcfg.full") },
            { value: "trispot_recurve", label: t("targetcfg.trispot_recurve") },
            { value: "trispot_compound", label: t("targetcfg.trispot_compound") },
          ]}
          value={faceType} onChange={chooseFace} />

        {/* Paramètres */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <NumInput label={t("solo_setup.ends")} value={ends} onChange={setEnds} min={1} max={30} testId="solo-ends" />
          <NumInput label={t("solo_setup.arrows_per_end")} value={arrows} onChange={setArrows} min={1} max={6} testId="solo-arrows" />
          <NumInput label={t("misc.num_series")} value={series} onChange={setSeries} min={1} max={4} testId="solo-series" />
        </div>

        <button data-testid="solo-start" onClick={start} disabled={creating}
          className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold uppercase tracking-wide px-10 py-4 rounded-md disabled:opacity-60 transition-colors">
          {creating ? t("solo_setup.creating") : t("solo_setup.cta")}
        </button>
      </main>
    </div>
  );
}